import { Controller } from 'react-hook-form';
import {
  TextField,
  Typography,
  Grid,
  Box,
} from '@mui/material';

// Import our custom components
import ImageUpload from './ImageUpload';

// Import utilities
import { getValidationRules } from '../utils/surveyValidation';
import { RESPONSIBILITY_OPTIONS } from '../utils/constants';

/**
 * ConditionalFields Component
 *
 * Renders the follow-up fields that appear when questions in an area have failing responses.
 * These fields collect additional information about issues that were identified.
 *
 * The conditional fields include:
 * - Issue explanation (required text area)
 * - Responsibility assignment (required dropdown: Facilities/Store)
 * - Image uploads (optional but recommended)
 *
 * @param {Object} props - Component props
 * @param {Object} props.control - React Hook Form control object
 * @param {Object} props.errors - Form validation errors
 * @param {string} props.fieldPrefix - Field name prefix (e.g., "1-2")
 * @param {number} props.sectionNumber - Display section number (1-based)
 * @param {number} props.areaNumber - Display area number (1-based)
 * @param {string} props.areaKey - The area name for display
 */
const ConditionalFields = ({
  control,
  errors,
  fieldPrefix,
  sectionNumber,
  areaNumber,
  areaKey,
}) => {
  // Field names for the conditional fields
  const explanationField = `${fieldPrefix}-explain`;
  const responsibilityField = `${fieldPrefix}-responsible`;
  const filesField = `${fieldPrefix}-files`;

  return (
    <Box>
      {/* Issue Explanation Field */}
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Typography
            variant='subtitle2'
            style={{
              fontFamily: 'Lato',
              fontSize: 16,
              fontWeight: 'bold',
              marginBottom: 8,
            }}
          >
            {sectionNumber}.{areaNumber} - Explain the issue(s)
          </Typography>

          <Controller
            name={explanationField}
            control={control}
            rules={getValidationRules('explanation', true)}
            render={({ field }) => (
              <TextField
                {...field}
                multiline
                minRows={6}
                maxRows={10}
                fullWidth
                variant='outlined'
                placeholder='Please provide a detailed explanation of the issues found in this area (minimum 10 characters)...'
                error={!!errors[explanationField]}
                helperText={
                  errors[explanationField]?.message ||
                  'Minimum 10 characters required'
                }
                sx={{ mb: 2 }}
              />
            )}
          />

          <Typography
            variant='body2'
            color='textSecondary'
            style={{
              fontFamily: 'Lato',
              fontSize: 12,
              fontStyle: 'italic',
              marginBottom: 16,
            }}
          >
            If multiple problems exist, please give an explanation and picture
            for each.
          </Typography>
        </Grid>

        {/* Responsibility Assignment Field */}
        <Grid item xs={12} sm={6}>
          <Typography
            variant='subtitle2'
            style={{
              fontFamily: 'Lato',
              fontSize: 16,
              fontWeight: 'bold',
              marginBottom: 8,
            }}
          >
            {sectionNumber}.{areaNumber} - Who is responsible?
          </Typography>

          <Controller
            name={responsibilityField}
            control={control}
            rules={getValidationRules('responsibility', true)}
            render={({ field }) => (
              <Box sx={{ mb: 2 }}>
                <select
                  {...field}
                  value={field.value || ''}
                  style={{
                    width: '100%',
                    padding: '16.5px 14px',
                    fontSize: '16px',
                    borderRadius: '4px',
                    border: errors[responsibilityField]
                      ? '2px solid #d32f2f'
                      : '1px solid #c4c4c4',
                    backgroundColor: '#fff',
                    fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
                    outline: 'none',
                    cursor: 'pointer',
                  }}
                >
                  {RESPONSIBILITY_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>

                {errors[responsibilityField] && (
                  <Typography
                    variant='caption'
                    color='error'
                    sx={{ mt: 0.5, ml: 1.75, display: 'block' }}
                  >
                    {errors[responsibilityField].message}
                  </Typography>
                )}
              </Box>
            )}
          />
        </Grid>

        {/* Image Upload Field */}
        <Grid item xs={12}>
          <Typography
            variant='subtitle2'
            style={{
              fontFamily: 'Lato',
              fontSize: 16,
              fontWeight: 'bold',
              marginBottom: 8,
            }}
          >
            {sectionNumber}.{areaNumber} - Image(s)
          </Typography>

          <Typography
            variant='body2'
            color='textSecondary'
            style={{
              fontFamily: 'Lato',
              fontSize: 12,
              marginBottom: 12,
            }}
          >
            Please upload images that clearly show the issues described above.
            Images help provide context and support the remediation process.
          </Typography>

          <Controller
            name={filesField}
            control={control}
            render={({ field }) => (
              <ImageUpload
                {...field}
                sectionIndex={sectionNumber}
                areaIndex={areaNumber}
                fieldName={filesField}
                error={errors[filesField]}
              />
            )}
          />
        </Grid>
      </Grid>

      {/* Additional Instructions */}
      <Box sx={{ mt: 3, p: 2, backgroundColor: '#f0f7ff', borderRadius: 1 }}>
        <Typography
          variant='body2'
          style={{
            fontFamily: 'Lato',
            fontSize: 13,
            color: '#1976d2',
          }}
        >
          <strong>Important:</strong> All fields above are required when issues
          are identified. This information will be used to create work orders
          and track remediation progress.
        </Typography>
      </Box>
    </Box>
  );
};

export default ConditionalFields;
